import { useCallback, useEffect, useMemo, useRef } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import {
  GetAssembliesByTemplateFieldsFiltersForFilterSideBarDocument,
  GetAssemblyPipeSpecDocument,
  useBulkPipeSpecUploadMutation,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { GraphQLError } from 'graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import {
  BulkPipeSpecUploadCsvHeadersEnum,
  CSV_VALIDATION_ERROR_CODE,
  SUPPORT_ERROR_MESSAGE,
} from '@/constants';
import { useSetAtom } from 'jotai';
import * as state from '../state';
import * as analysisState from '@/components/Analysis/state';
import { PipeSpecUploadErrorTableModal } from './PipeSpecUploadErrorTableModal';
import { Typography } from '@mui/material';
import { PipeSpecUploadErrorDialog } from './PipeSpecUploadErrorDialog';
import { theme } from '@/theme/theme';
import { FileFixingUploadLoadingModal } from '../../shared';
import { downloadArrayAsXlsx } from '@/utils/xlsx';
// import { parseUploadedFile } from '@/utils/fileProcessing';

type Props = {
  anchorEl: null | HTMLElement;
  setAnchorEl: (value: null | HTMLElement) => void;
};

export const PipeSpecUploadMenu = ({ anchorEl, setAnchorEl }: Props) => {
  const setPipeSpecUploadState = useSetAtom(state.pipeSpecUploadState);
  const setApplyFilters = useSetAtom(analysisState.applyFilters);
  const open = Boolean(anchorEl);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (fileRef?.current) {
      setPipeSpecUploadState((previous) => ({ ...previous, inputRef: fileRef.current }));
    }
  }, [setPipeSpecUploadState]);

  const handleClose = useCallback(() => {
    if (fileRef.current) fileRef.current.value = '';
    setAnchorEl(null);
  }, [setAnchorEl]);

  const structureId = useGetStructureId();
  const [upload, { loading }] = useBulkPipeSpecUploadMutation({
    refetchQueries: [
      GetAssemblyPipeSpecDocument,
      GetAssembliesByTemplateFieldsFiltersForFilterSideBarDocument,
    ],
    onCompleted: () => {
      // Still, a hack to refetch the EquipmentTable
      setApplyFilters(true);
    },
  });
  const { showSnackBar } = useSnackBarMessage({});

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setPipeSpecUploadState((previous) => ({ ...previous, state: 'idle' }));

      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const fileData = e.target?.result;
          if (!fileData) return;

          // Convert to base64 string
          const csvData = typeof fileData === 'string' 
            ? fileData.replace(/^data:.*;base64,/, '')
            : '';
          
          // Pass the XLSX file data as a base64 string to the backend
          upload({
            variables: {
              input: {
                csv: csvData,
                structureId,
              },
            },
          })
          .then((response) => {
            const count = response.data?.bulkPipeSpecUpload?.updatedCount;
            const message = `${count} components have been successfully updated.`;
            showSnackBar(message, 'success');
          })
          .catch((error) => {
            if (fileRef.current) {
              fileRef.current.value = '';
            }
            
            const gqlError: GraphQLError | undefined = error?.graphQLErrors?.[0];

            if (!gqlError) {
              showSnackBar(error.message || SUPPORT_ERROR_MESSAGE, 'error');
              return;
            }
            
            if (gqlError.extensions?.code === CSV_VALIDATION_ERROR_CODE) {
              setPipeSpecUploadState((previous) => ({
                ...previous,
                state: 'csv-error',
                error: gqlError,
                inputCsv: fileData as string,
              }));
              return;
            } else {
              setPipeSpecUploadState((previous) => ({
                ...previous,
                state: 'generic-error',
                error: gqlError,
                inputCsv: fileData as string,
              }));
            }
          });
        } catch (innerError) {
          showSnackBar('Error processing file data. Please try again.', 'error');
          if (fileRef.current) {
            fileRef.current.value = '';
          }
        }
      };
      
      reader.onerror = () => {
        showSnackBar('Error reading file. Please try again.', 'error');
        if (fileRef.current) {
          fileRef.current.value = '';
        }
      };
      
      // Read as DataURL to get base64 string
      reader.readAsDataURL(file);
    } catch (outerError) {
      showSnackBar('Error handling file. Please try again.', 'error');
      if (fileRef.current) {
        fileRef.current.value = '';
      }
    }
  };

  const MENU_ITEMS: Array<{
    title: string;
    description: string;
    onClick?: () => void;
    disabled?: boolean;
  }> = useMemo(
    () => [
      {
        title: 'Upload new file',
        description: 'Values for the pipe spec should be in inches.',
        onClick: () => {
          fileRef?.current?.click?.();
          handleClose();
        },
      },
      {
        title: 'Download template',
        description: 'Download template to view all the required columns.',
        onClick: () => {
          downloadArrayAsXlsx(
            Object.values(BulkPipeSpecUploadCsvHeadersEnum),
            'pipe-spec-upload-template.xlsx'
          );
        },
      },
    ],
    [handleClose]
  );

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{
          mt: 2,
        }}
      >
        {MENU_ITEMS.map((item, index) => (
          <MenuItem
            disabled={item.disabled}
            key={index}
            onClick={item.onClick}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              my: 1,
            }}
          >
            <Typography color={theme.palette.primary.main}>{item.title}</Typography>
            <Typography color={theme.palette.primary.light}>{item.description}</Typography>
          </MenuItem>
        ))}
      </Menu>
      <input
        style={{ display: 'none' }}
        type="file"
        accept=".xlsx"
        onChange={handleFileChange}
        ref={fileRef}
      />
      <PipeSpecUploadErrorTableModal />
      <PipeSpecUploadErrorDialog />
      <FileFixingUploadLoadingModal open={loading} />
    </>
  );
};
